import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Autocomplete,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { LoadingButton } from "@mui/lab";
import { SuittService } from "../../../services/index";
import { snackActions } from "../../../utils/SnackBarUtils";
import { setJWTToken, setUserInfoToLocalStorage } from "../../../utils";
import { StoreContext } from "../../../App";

const initFormState = {
  emailError: false,
  passError: false,
  passLengthError: false,
  repeatPassError: false,
  repeatPassLengthError: false,
  firstNameError: false,
  lastNameError: false,
  accountTypeError: false,
  genderError: false,
  orgUnitError: false,
};

function Register() {
  const suittService = useMemo(() => new SuittService(), []);
  const { auth } = useContext(StoreContext);
  const initState = {
    email: "",
    password: "",
    repeatPass: "",
    firstName: "",
    lastName: "",
    accountType: "",
    orgUnitCode: "",
    gender: "",
    sending: false,
    errors: [],
  };
  const [registerForm, setRegisterForm] = useState(initState);
  const [registerFormState, setRegisterFormState] = useState(initFormState);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [orgUnits, setOrgUnits] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const result = await suittService.getOrgUnits();
      if (result?.status && Array.isArray(result.data)) {
        const list = result.data;
        const byParent = new Map();
        for (const u of list) {
          const key = u.parentCode ?? "__ROOT__";
          const arr = byParent.get(key) || [];
          arr.push(u);
          byParent.set(key, arr);
        }
        const ordered = [];
        const walk = (parentCode, depth) => {
          const key = parentCode ?? "__ROOT__";
          const arr = (byParent.get(key) || []).sort((a, b) =>
            a.nameUk.localeCompare(b.nameUk)
          );
          for (const u of arr) {
            ordered.push({
              ...u,
              _depth: depth,
              _indentLabel: `${"— ".repeat(depth)}${u.nameUk}`,
            });
            walk(u.code, depth + 1);
          }
        };
        walk(null, 0);
        setOrgUnits(ordered);
      }
    })();
  }, [suittService]);

  const doValidateForm = () => {
    const {
      email,
      password,
      repeatPass,
      firstName,
      lastName,
      accountType,
      orgUnitCode,
      gender,
    } = registerForm;

    const errors = { ...initFormState };
    const emailRegex = /^\S+@\S+\.[A-Za-z]{2,}$/;
    if (email === "") errors.emailError = true;
    else if (!emailRegex.test(email)) errors.emailError = true;
    if (password === "") errors.passError = true;
    if (repeatPass === "") errors.repeatPassError = true;
    if (password.length < 8) errors.passLengthError = true;
    if (repeatPass.length < 8) errors.repeatPassLengthError = true;
    if (password !== repeatPass) {
      errors.passError = true;
      errors.repeatPassError = true;
    }
    if (firstName === "") errors.firstNameError = true;
    if (lastName === "") errors.lastNameError = true;
    if (accountType === "") errors.accountTypeError = true;
    if (gender === "") errors.genderError = true;
    if (orgUnitCode === "") errors.orgUnitError = true;

    // If at least one of errors is true - set false state
    const state = !Object.values(errors).some((error) => error);

    setRegisterFormState({
      ...registerFormState,
      ...errors,
    });

    return state;
  };

  const doRegister = async () => {
    const isValidForm = doValidateForm();
    if (!isValidForm) return;

    setRegisterForm({ ...registerForm, sending: true, errors: [] });

    const payload = { ...registerForm };
    const result = await suittService.doRegister(payload);

    if (result.status) {
      snackActions.success("Реєстрація пройшла успішно");

      setJWTToken(result.data.token);
      setUserInfoToLocalStorage(result.data);
      auth.setUser(result.data);
      return navigate(`/cabinet/users/${result.data._id}`);
    } else {
      setRegisterForm({
        ...initState,
        errors: result.errors,
        sending: false,
      });

      snackActions.error(result.errors[0].message);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      columns={12}
      alignItems={"center"}
      sx={{
        flex: 1,
      }}
    >
      <Grid item xs={12} md={12}>
        <Card
          style={{
            maxWidth: 480,
            padding: "32px 24px",
            margin: "60px auto 100px",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <CardContent>
            <Typography gutterBottom variant="h5" align="center">
              {"Реєстрація облікового запису"}
            </Typography>
            {registerForm.errors.map((error) => (
              <Alert severity="error">{error.message}</Alert>
            ))}
            <FormControl sx={{ mt: 3 }} variant="standard">
              <Grid container spacing={2}>
                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    type="email"
                    placeholder={"Вкажіть ваш email"}
                    label="Email"
                    variant="standard"
                    fullWidth
                    required
                    error={registerFormState.emailError}
                    helperText={
                      registerForm.email === ""
                        ? "Вкажіть email"
                        : !/^\S+@\S+\.[A-Za-z]{2,}$/.test(registerForm.email)
                        ? "Вкажіть коректний email"
                        : ""
                    }
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        email: e.target.value,
                      })
                    }
                    value={registerForm.email}
                  />
                </Grid>
                {/* Пароли в один ряд */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    placeholder={"Пароль"}
                    label={"Вкажіть пароль"}
                    variant="standard"
                    fullWidth
                    required
                    error={registerFormState.passError}
                    helperText={
                      registerForm.password === ""
                        ? "Пароль є обов'язковим"
                        : registerForm.password.length < 8
                        ? "Мінімальна довжина пароля 8 символів"
                        : ""
                    }
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        password: e.target.value,
                      })
                    }
                    value={registerForm.password}
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    placeholder={"Повторіть свій пароль"}
                    label={"Повторіть пароль"}
                    variant="standard"
                    fullWidth
                    required
                    error={registerFormState.repeatPassError}
                    helperText={
                      registerForm.repeatPass === ""
                        ? "Повторіть пароль"
                        : registerForm.repeatPass.length < 8
                        ? "Мінімальна довжина пароля 8 символів"
                        : registerForm.password !== registerForm.repeatPass
                        ? "Паролі не співпадають"
                        : ""
                    }
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        repeatPass: e.target.value,
                      })
                    }
                    value={registerForm.repeatPass}
                    type={showRepeatPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowRepeatPassword((v) => !v)}
                            aria-label="toggle repeat password visibility"
                          >
                            {showRepeatPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {/* Ім'я/Прізвище */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    placeholder={"Вкажіть ваше ім'я"}
                    label={"Ім'я"}
                    variant="standard"
                    fullWidth
                    required
                    error={registerFormState.firstNameError}
                    helperText={
                      registerForm.firstName === "" ? "Вкажіть ім'я" : ""
                    }
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        firstName: e.target.value,
                      })
                    }
                    value={registerForm.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    placeholder={"Вкажіть ваше прізвище"}
                    label={"Прізвище"}
                    variant="standard"
                    fullWidth
                    required
                    error={registerFormState.lastNameError}
                    helperText={
                      registerForm.lastName === "" ? "Вкажіть прізвище" : ""
                    }
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        lastName: e.target.value,
                      })
                    }
                    value={registerForm.lastName}
                  />
                </Grid>
                {/* Підрозділ */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={registerFormState.orgUnitError}>
                    <Autocomplete
                      options={orgUnits}
                      getOptionLabel={(o) => o._indentLabel || o.nameUk || ""}
                      isOptionEqualToValue={(o, v) => o.code === v.code}
                      value={
                        orgUnits.find(
                          (o) => o.code === registerForm.orgUnitCode
                        ) || null
                      }
                      onChange={(e, val) =>
                        setRegisterForm({
                          ...registerForm,
                          orgUnitCode: val?.code || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={"Підрозділ"}
                          variant="standard"
                        />
                      )}
                    />
                    {registerFormState.orgUnitError && (
                      <FormHelperText>Оберіть підрозділ</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {/* Тип профілю */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={registerFormState.accountTypeError}
                  >
                    <InputLabel id="label-type" shrink>
                      Тип профілю
                    </InputLabel>
                    <Select
                      labelId="label-type"
                      id="select-type"
                      label={"Тип профілю"}
                      value={registerForm.accountType}
                      onChange={(e) => {
                        const accountType = e.target.value;
                        setRegisterForm({ ...registerForm, accountType });
                      }}
                    >
                      <MenuItem value={"Teacher"}>
                        {"Працівник/Викладач"}
                      </MenuItem>
                      <MenuItem value={"HR"}>{"Відділ кадрів"}</MenuItem>
                      <MenuItem value={"ViceRectorForScientificWork"} disabled>
                        {"Начальник НМВ"}
                      </MenuItem>
                      <MenuItem value={"ViceRectorForEducationalWork"} disabled>
                        {"Проректор з навчальної роботи"}
                      </MenuItem>
                      <MenuItem value={"DeanOfTheFaculty"} disabled>
                        {"Декан факультету"}
                      </MenuItem>
                      <MenuItem value={"HeadOfTheDepartment"} disabled>
                        {"Завідувач кафедрою"}
                      </MenuItem>
                      <MenuItem value={"HR"} disabled>
                        {"Проректор з наукової роботи"}
                      </MenuItem>
                    </Select>
                    {registerFormState.accountTypeError && (
                      <FormHelperText>Оберіть тип профілю</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {/* Стать */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={registerFormState.genderError}>
                    <InputLabel id="label-gender" shrink>
                      Стать
                    </InputLabel>
                    <Select
                      labelId="label-gender"
                      id="select-gender"
                      label={"Стать"}
                      value={registerForm.gender}
                      onChange={(e) => {
                        const gender = e.target.value;
                        setRegisterForm({ ...registerForm, gender });
                      }}
                    >
                      <MenuItem value={"чол"}>{"Чоловік"}</MenuItem>
                      <MenuItem value={"жін"}>{"Жінка"}</MenuItem>
                    </Select>
                    {registerFormState.genderError && (
                      <FormHelperText>Оберіть стать</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} display={"flex"} justifyContent={"center"}>
                  <LoadingButton
                    loading={registerForm.sending}
                    size="large"
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{ maxWidth: 420, mt: 1 }}
                    color="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      setTimeout(async () => {
                        await doRegister();
                      }, 100);
                    }}
                  >
                    {"Зареєструватись"}
                  </LoadingButton>
                </Grid>
              </Grid>
            </FormControl>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default Register;
